import React, { useState, useEffect, useMemo } from "react";
import "../../../styles/VocabularyStudy.css";
import { workplaceVocabulary } from "../../../data/vocabulary/workplace";
import { commercialVocabulary } from "../../../data/vocabulary/commercial";
import { residentialVocabulary } from "../../../data/vocabulary/residential";
import { publicFacilitiesVocabulary } from "../../../data/vocabulary/publicFacilities";
import { industrialVocabulary } from "../../../data/vocabulary/industrial";
import { useAuth } from "../../../contexts/AuthContext";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { db } from "../../../firebase";
import { useNavigate } from "react-router-dom";
import { freeContent } from "../../../utils/freeContent";

const VocabularyStudy = ({ onBack }) => {
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showMeaning, setShowMeaning] = useState(false);
  const [hasListenedAudio, setHasListenedAudio] = useState(false);
  const [isAutoPlay, setIsAutoPlay] = useState(false);
  const [starredWords, setStarredWords] = useState({});
  const [showStarredOnly, setShowStarredOnly] = useState(false);
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [localStarredWords, setLocalStarredWords] = useState({});
  const [showVideoModal, setShowVideoModal] = useState(false);

  const subCategories = {
    workplace: {
      id: "workplace",
      title: "업무 공간",
      description: "사무실, 회의실, 작업장 등의 업무 공간 관련 단어",
      examples: ["desk", "computer", "meeting room"],
      vocabulary: workplaceVocabulary,
    },
    commercial: {
      id: "commercial",
      title: "상업 공간",
      description: "상점, 쇼핑몰, 식당 등의 상업 시설 관련 단어",
      examples: ["store", "restaurant", "shopping mall"],
      vocabulary: commercialVocabulary,
    },
    publicFacilities: {
      id: "publicFacilities",
      title: "공공 시설",
      description: "병원, 박물관, 체육관 등의 공공 시설 관련 단어",
      examples: ["hospital", "museum", "gym"],
      vocabulary: publicFacilitiesVocabulary,
    },
    residential: {
      id: "residential",
      title: "주거 공간",
      description: "아파트, 주택, 거실 등의 주거 공간 관련 단어",
      examples: ["apartment", "house", "living room"],
      vocabulary: residentialVocabulary,
    },
    industrial: {
      id: "industrial",
      title: "산업 공간",
      description: "공장, 창고, 건설 현장 등의 산업 시설 관련 단어",
      examples: ["factory", "warehouse", "construction site"],
      vocabulary: industrialVocabulary,
    },
  };

  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
    setCurrentIndex(0);
    setShowMeaning(false);
    setHasListenedAudio(false);
    setIsAutoPlay(false);

    console.log("카테고리 선택됨:", {
      카테고리: category,
      자동재생: false,
    });
  };

  const handleCloseModal = () => {
    setSelectedCategory(null);
    setCurrentIndex(0);
    setShowMeaning(false);
    setHasListenedAudio(false);
    setIsAutoPlay(false);

    console.log("모달 닫힘:", {
      자동재생초기화: false,
    });
  };

  const toggleAutoPlay = () => {
    const newState = !isAutoPlay;
    setIsAutoPlay(newState);

    if (newState) {
      setShowMeaning(false);
      setHasListenedAudio(false);
    }

    console.log("자동 재생 상태 변경:", {
      이전상태: isAutoPlay,
      새상태: newState,
    });
  };

  // currentVocabulary를 useMemo로 최적화
  const currentVocabulary = useMemo(() => {
    // 즐겨찾기 모드면
    if (showStarredOnly) {
      // 그냥 Firebase에서 가져온 즐겨찾기 단어들 전부 보여주기
      const starredList = Object.values(starredWords).filter(
        (word) => word.isStarred
      );
      console.log("즐겨찾기 필터링:", {
        전체단어수: Object.keys(starredWords).length,
        필터링후단어수: starredList.length,
        필터링된단어목록: starredList,
      });
      return starredList;
    }

    // 아니면 현재 카테고리 단어 보여주기
    return subCategories[selectedCategory]?.vocabulary || [];
  }, [showStarredOnly, selectedCategory, starredWords]);

  const hasWords = currentVocabulary.length > 0;

  // 다음 단어로 이동
  const handleNext = () => {
    const filteredVocab = currentVocabulary;
    if (currentIndex < filteredVocab.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setShowMeaning(false);
      setHasListenedAudio(false);
    } else {
      setIsAutoPlay(false); // 마지막 단어에서 자동 재생 중지
    }
  };

  // 기존 handlePrev 함수 수정
  const handlePrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      setShowMeaning(false);
      setHasListenedAudio(false);
    }
  };

  // 오디오 재생
  const handlePlayAudio = (e) => {
    if (e) e.stopPropagation();
    const currentVocab = currentVocabulary;
    const currentWord = currentVocab[currentIndex];
    if (!currentWord) return;

    // 즐겨찾기 모드일 때는 해당 단어의 카테고리를 사용
    const category = showStarredOnly ? currentWord.category : selectedCategory;
    const audioPath = `/audios/vocabulary/${category}/word${currentWord.audioId}.mp3`;

    console.log("오디오 재생:", {
      현재인덱스: currentIndex,
      단어: currentWord.word,
      audioId: currentWord.audioId,
      카테고리: category,
      오디오경로: audioPath,
    });

    const audio = new Audio(audioPath);
    audio.onended = () => {
      setHasListenedAudio(true);
    };

    audio.play().catch((error) => {
      console.error("오디오 재생 에러:", error);
      setHasListenedAudio(true);
    });
  };

  const handleBack = () => {
    navigate("/part1"); // Part1_1 컴포넌트로 이동
  };

  // 자동 재생을 위한 useEffect 추가
  useEffect(() => {
    let timeoutId;

    if (isAutoPlay && hasWords) {
      const playCurrentWord = () => {
        handlePlayAudio();
        timeoutId = setTimeout(() => {
          setShowMeaning(true);
          timeoutId = setTimeout(() => {
            setShowMeaning(false);
            handleNext();
          }, 2000);
        }, 2000);
      };

      playCurrentWord();
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [isAutoPlay, currentIndex, hasWords]);

  // Firebase 데이터 로딩
  useEffect(() => {
    const loadStarredWords = async () => {
      if (!currentUser) return;

      try {
        const starredRef = doc(
          db,
          "users",
          currentUser.uid,
          "starredWords",
          "all"
        );
        const docSnap = await getDoc(starredRef);
        const data = docSnap.exists() ? docSnap.data() : {};
        setStarredWords(data);
        setLocalStarredWords(data); // 로컬 캐시도 동기화

        console.log("Firebase 초기 데이터 로드:", {
          데이터존재여부: docSnap.exists(),
          즐겨찾기수: Object.keys(data).length,
          데이터: data,
        });
      } catch (error) {
        console.error("즐겨찾기 데이터 로드 실패:", error);
      }
    };

    loadStarredWords();
  }, [currentUser]);

  // toggleStar 함수 수정
  const toggleStar = async (word) => {
    if (!currentUser) return;

    const category = showStarredOnly ? word.category : selectedCategory;
    const wordKey = `${category}_${word.audioId}`;

    // 즉시 UI 업데이트를 위한 상태 복사
    const newLocalStarredWords = { ...localStarredWords };

    // 현재 즐겨찾기 상태 확인
    const isCurrentlyStarred =
      !!localStarredWords[wordKey] || !!starredWords[wordKey];

    // 즉시 UI 업데이트 (낙관적 업데이트)
    if (isCurrentlyStarred) {
      // 즐겨찾기 해제
      delete newLocalStarredWords[wordKey];
    } else {
      // 즐겨찾기 추가
      newLocalStarredWords[wordKey] = {
        category: category,
        audioId: word.audioId,
        word: word.word,
        meaning: word.meaning,
        isStarred: true,
      };
    }

    // 즉시 로컬 상태 업데이트
    setLocalStarredWords(newLocalStarredWords);
    setStarredWords(newLocalStarredWords);

    // 즐겨찾기 모드일 때 즉시 UI 업데이트
    if (showStarredOnly && isCurrentlyStarred) {
      // 즐겨찾기가 더 이상 없으면 일반 모드로 전환
      if (Object.keys(newLocalStarredWords).length === 0) {
        setShowStarredOnly(false);
        setCurrentIndex(0);
      }
      // 마지막 단어였으면 이전 단어로
      else if (
        currentIndex === currentVocabulary.length - 1 &&
        currentIndex > 0
      ) {
        setCurrentIndex(currentIndex - 1);
      }
    }

    try {
      // 백그라운드에서 Firebase 저장
      const starredRef = doc(
        db,
        "users",
        currentUser.uid,
        "starredWords",
        "all"
      );
      await setDoc(starredRef, newLocalStarredWords);

      console.log("Firebase 저장 성공:", {
        단어: word.word,
        즐겨찾기상태: !isCurrentlyStarred,
      });
    } catch (error) {
      console.error("Firebase 저장 실패:", error);

      // 에러 발생 시 원래 상태로 복구 (롤백)
      const originalStarredWords = { ...starredWords };
      setLocalStarredWords(originalStarredWords);
      setStarredWords(originalStarredWords);

      // 사용자에게 알림
      alert("즐겨찾기 상태 저장에 실패했습니다. 다시 시도해주세요.");
    }
  };

  if (!selectedCategory) {
    return (
      <div className="vocabulary-study">
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "0 20px",
          }}
        >
          <button className="back-button" onClick={onBack}>
            ← 메인 학습 유형으로 돌아가기
          </button>
          <button
            className="back-button"
            style={{ backgroundColor: "#4a4a7a" }}
            onClick={() => setShowVideoModal(true)}
          >
            설명 동영상 보기
          </button>
        </div>
        <div className="category-selection">
          <h2>단어 학습</h2>
          <div className="category-grid">
            {Object.values(subCategories).map((category) => (
              <div
                key={category.id}
                className="category-card"
                onClick={() => handleCategorySelect(category.id)}
              >
                <h3>{category.title}</h3>
                <p>{category.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* 동영상 모달 */}
        {showVideoModal && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.8)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 1100,
            }}
            onClick={() => setShowVideoModal(false)}
          >
            <div
              style={{
                background: "#222",
                borderRadius: "8px",
                padding: "20px",
                width: "90%",
                maxWidth: "800px",
                position: "relative",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                style={{
                  position: "absolute",
                  top: "10px",
                  right: "10px",
                  background: "none",
                  border: "none",
                  color: "white",
                  fontSize: "24px",
                  cursor: "pointer",
                }}
                onClick={() => setShowVideoModal(false)}
              >
                ×
              </button>
              <video
                src="/videos/vocabulary-explanation.mp4"
                controls
                autoPlay
                style={{ width: "100%", marginTop: "20px" }}
              />
            </div>
          </div>
        )}
      </div>
    );
  }

  const currentCategory = subCategories[selectedCategory];
  const currentWord = showStarredOnly
    ? currentVocabulary[currentIndex] // 즐겨찾기 모드일 때는 필터링된 단어 목록에서 가져옴
    : currentCategory.vocabulary?.[currentIndex]; // 일반 모드일 때는 카테고리 단어에서 가져옴

  return (
    <div className="vocabulary-study-container">
      <button className="back-button" onClick={onBack}>
        ← 학습 유형 선택으로 돌아가기
      </button>

      {selectedCategory && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="header">
              <button className="modal-close-btn" onClick={handleCloseModal}>
                ×
              </button>
              <h2 className="title">
                {subCategories[selectedCategory].title} 단어
              </h2>
            </div>

            <div className="vocabulary-mode-wrapper">
              <div className="vocabulary-mode-selector">
                <button
                  className={`vocabulary-mode-button ${
                    isAutoPlay ? "active" : ""
                  }`}
                  onClick={toggleAutoPlay}
                >
                  {isAutoPlay ? "⏸ 자동 재생 중" : "▶ 자동 재생"}
                </button>
                <button
                  className={`vocabulary-mode-button ${
                    showStarredOnly ? "active" : ""
                  }`}
                  onClick={() => {
                    if (
                      !showStarredOnly &&
                      Object.keys(starredWords).length === 0
                    ) {
                      alert("즐겨찾기한 단어가 없습니다.");
                      return;
                    }
                    setShowStarredOnly(!showStarredOnly);
                    setCurrentIndex(0);
                  }}
                >
                  {showStarredOnly ? "⭐ 즐겨찾기만" : "전체 단어"}
                </button>
              </div>
            </div>

            <div className="word-display">
              {!isAutoPlay ? (
                <button className="audio-button" onClick={handlePlayAudio}>
                  <img src="/icons/play.svg" alt="듣기" /> 단어 듣기
                </button>
              ) : (
                <button
                  className="audio-button pause"
                  onClick={() => setIsAutoPlay(false)}
                >
                  <img src="/icons/pause.svg" alt="일시정지" /> 일시정지
                </button>
              )}
            </div>

            <div className="meaning-container">
              {!showMeaning ? (
                !isAutoPlay && (
                  <button
                    className="show-meaning-button"
                    onClick={() => setShowMeaning(true)}
                    disabled={!hasListenedAudio}
                  >
                    {hasListenedAudio ? "뜻 보기" : "단어를 먼저 들어보세요"}
                  </button>
                )
              ) : (
                <div className="meaning-content">
                  <div className="word-header">
                    <h2
                      className="word-text"
                      style={{ cursor: "pointer" }}
                      onClick={() => toggleStar(currentWord)}
                    >
                      {currentWord?.word}
                    </h2>
                    <button
                      className="star-button"
                      onClick={() => toggleStar(currentWord)}
                    >
                      {showStarredOnly
                        ? "⭐"
                        : starredWords[
                            `${selectedCategory}_${currentWord?.audioId}`
                          ]?.isStarred
                        ? "⭐"
                        : "☆"}
                    </button>
                  </div>
                  <h3>{currentWord?.meaning}</h3>
                  {currentWord?.imageUrl && (
                    <img
                      src={currentWord.imageUrl}
                      alt={currentWord.word}
                      className="vocabulary-image"
                      style={{ maxWidth: "200px", margin: "10px 0" }}
                    />
                  )}
                  <button
                    className="hide-meaning-button"
                    onClick={() => setShowMeaning(false)}
                  >
                    숨기기
                  </button>
                </div>
              )}
            </div>

            <div className="navigation">
              <button onClick={handlePrev} disabled={currentIndex === 0}>
                이전
              </button>
              <span>
                {currentIndex + 1} / {currentVocabulary.length}
              </span>
              <button
                onClick={handleNext}
                disabled={currentIndex === currentVocabulary.length - 1}
              >
                다음
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VocabularyStudy;
